import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { LanguageService } from "./language.service";
import { UserService } from "./user.service";
import { DOCUMENT, isPlatformBrowser } from "@angular/common";


export enum ActiveTab {
  GIPANT = 'GIPANT',
  HENTERPANT = 'HENTERPANT',
  TEAMS_OR_PLAYERS = 'TEAMS_OR_PLAYERS'
}

@Injectable({
  providedIn: 'root'
})
export class TabService {
  private _router: Router = inject(Router);
  private _languageService: LanguageService = inject(LanguageService);
  private _userService: UserService = inject(UserService);
  private _platformId = inject(PLATFORM_ID);
  private _document = inject(DOCUMENT);
  private activeTabSubject: BehaviorSubject<ActiveTab> = new BehaviorSubject<ActiveTab>(ActiveTab.GIPANT);
  activeTab$: Observable<ActiveTab> = this.activeTabSubject.asObservable();

  constructor() {
    this._router.events
      .pipe(
        filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        setTimeout(() => {
          this.updateTabBasedOnUrl(event.urlAfterRedirects);
        }, 250);
      });
  }

  async initTabFromUrl(): Promise<void> {
    return new Promise((resolve, reject) => {
      const path: any = isPlatformBrowser(this._platformId)
        ? this._document.defaultView?.location.pathname.replace(/\/+$/, '')
        : this._router.url.split('?')[0].replace(/\/+$/, '');

      if (path && path.includes('/henter-pant')) {
        this.setActiveTab(ActiveTab.HENTERPANT);
      } else if (path && (path.includes('/teams/') || path.includes('/players/'))) {
        this.setActiveTab(ActiveTab.TEAMS_OR_PLAYERS);
      } else {
        this.setActiveTab(ActiveTab.GIPANT);
      }

      setTimeout(() => resolve(), 0);
    });
  }

  private updateTabBasedOnUrl(url: string): void {
    const normalizedUrl: string = url.replace(/\/+$/, '');
    const HENTER_PANT_BASE_URL: string = `/${this._languageService.currentLang}/henter-pant`;

    if (normalizedUrl.includes('players') || normalizedUrl.includes('teams')) {
      this.setActiveTab(ActiveTab.TEAMS_OR_PLAYERS);
    } else if (normalizedUrl.startsWith(HENTER_PANT_BASE_URL)) {
      this.setActiveTab(ActiveTab.HENTERPANT);
    } else {
      this.setActiveTab(ActiveTab.GIPANT);
    }
  }


  setActiveTab(tab: ActiveTab): void {
    const GIPANT_BODY_CLASS_NAME: string = 'gipant-active';
    const HENTERPANT_BODY_CLASS_NAME: string = 'henterpant-active';
    const TEAMS_OR_PLAYERS: string = 'teams-players-active';

    this.activeTabSubject.next(tab);

    const body: HTMLElement = this._document.body;
    const html: HTMLElement = this._document.documentElement;

    if (body) {
      body.classList.remove(GIPANT_BODY_CLASS_NAME, HENTERPANT_BODY_CLASS_NAME, TEAMS_OR_PLAYERS);
      html.classList.remove(GIPANT_BODY_CLASS_NAME, HENTERPANT_BODY_CLASS_NAME, TEAMS_OR_PLAYERS);

      if (tab === ActiveTab.HENTERPANT) {
        body.classList.add(HENTERPANT_BODY_CLASS_NAME);
        html.classList.add(HENTERPANT_BODY_CLASS_NAME);
        if (isPlatformBrowser(this._platformId)) {
          this._userService.changeCurrentUser('team');
        }
      } else if (tab === ActiveTab.GIPANT) {
        if (isPlatformBrowser(this._platformId)) {
          this._userService.changeCurrentUser('end-consumer');
        }
        body.classList.add(GIPANT_BODY_CLASS_NAME);
        html.classList.add(GIPANT_BODY_CLASS_NAME);
      } else if (tab === ActiveTab.TEAMS_OR_PLAYERS) {
        if (isPlatformBrowser(this._platformId)) {
          this._userService.changeCurrentUser('end-consumer');
        }
        body.classList.add(TEAMS_OR_PLAYERS);
        html.classList.add(TEAMS_OR_PLAYERS);
      }
    }
  }

  get currentTab(): ActiveTab {
    return this.activeTabSubject.value;
  }
}

